import { useEffect, useState } from "react";
import { getSectors } from "@/api/companies";

interface CacheEntry {
  promise: Promise<string[]>;
  value: string[] | null;
}

/** Shared per-slug cache so a page that mounts the combobox in three
 *  places (filter / detail / create dialog) only fetches once, and a
 *  consumer that calls `refresh()` after saving a new sector pushes
 *  the update to every other live consumer. */
const cache = new Map<string, CacheEntry>();
const subscribers = new Map<string, Set<() => void>>();

function subscribe(slug: string, fn: () => void): () => void {
  let set = subscribers.get(slug);
  if (!set) {
    set = new Set();
    subscribers.set(slug, set);
  }
  set.add(fn);
  return () => {
    set?.delete(fn);
  };
}

function notify(slug: string) {
  subscribers.get(slug)?.forEach((fn) => fn());
}

function load(slug: string): Promise<string[]> {
  const existing = cache.get(slug);
  if (existing) return existing.promise;
  const entry: CacheEntry = {
    value: null,
    promise: getSectors(slug)
      .then((res) => {
        entry.value = res.sectors;
        notify(slug);
        return res.sectors;
      })
      .catch(() => {
        entry.value = [];
        notify(slug);
        return [];
      }),
  };
  cache.set(slug, entry);
  return entry.promise;
}

export interface UseSectorsResult {
  sectors: string[];
  loading: boolean;
  /** Force a refetch — call after saving a company with a sector that
   *  may not yet be in the cached list. */
  refresh: () => void;
}

export function useSectors(slug: string | null | undefined): UseSectorsResult {
  const [, force] = useState(0);

  useEffect(() => {
    if (!slug) return;
    void load(slug);
    return subscribe(slug, () => force((n) => n + 1));
  }, [slug]);

  const entry = slug ? cache.get(slug) : undefined;
  return {
    sectors: entry?.value ?? [],
    loading: !!slug && entry?.value == null,
    refresh: () => {
      if (!slug) return;
      cache.delete(slug);
      void load(slug);
    },
  };
}
